import type Redis from 'ioredis';

/**
 * Delete all keys that match a Redis glob-style pattern using SCAN in batches.
 * Uses UNLINK (non-blocking) and falls back to DEL if UNLINK isn't available.
 * @param redis ioredis instance
 * @param pattern e.g. "highlights:*" or "highlights:01:*" or "highlights:idx:*"
 * @param count SCAN COUNT hint per iteration (higher = faster, more CPU)
 * @returns number of keys scheduled for deletion (unlink/del ops issued)
 */
export async function invalidateByPattern(
  redis: Redis,
  pattern: string,
  count = 1000
): Promise<number> {
  let cursor = '0';
  let deleted = 0;

  do {
    // SCAN cursor MATCH pattern COUNT count
    const [nextCursor, keys] = await redis.scan(
      cursor,
      'MATCH',
      pattern,
      'COUNT',
      count
    );
    cursor = nextCursor;

    if (keys.length > 0) {
      const pipeline = redis.pipeline();
      // Prefer UNLINK to avoid blocking; fallback to DEL if command unsupported.
      const useUnlink = typeof (pipeline as any).unlink === 'function';

      for (const k of keys) {
        if (useUnlink) (pipeline as any).unlink(k);
        else pipeline.del(k);
      }

      const res = await pipeline.exec();
      deleted += res?.length ?? 0;
    }
  } while (cursor !== '0');

  return deleted;
}

/**
 * Convenience: invalidate multiple patterns.
 */
export async function invalidateManyPatterns(
  redis: Redis,
  patterns: string[],
  count = 1000
): Promise<number> {
  let total = 0;
  for (const p of patterns) {
    total += await invalidateByPattern(redis, p, count);
  }
  return total;
}
